import { useState } from "react";

const BODY_PARTS = [
  { key: "front_bumper", label: "Első lökhárító" },
  { key: "hood", label: "Motorháztető" },
  { key: "right_front_fender", label: "Jobb első sárvédő" },
  { key: "right_front_door", label: "Jobb első ajtó" },
  { key: "roof", label: "Tető" },
  { key: "right_rear_door", label: "Jobb hátsó ajtó" },
  { key: "right_rear_fender", label: "Jobb hátsó sárvédő" },
  { key: "trunk", label: "Csomagtér fedél" },
  { key: "rear_bumper", label: "Hátsó lökhárító" },
  { key: "left_front_fender", label: "Bal első sárvédő" },
  { key: "left_front_door", label: "Bal első ajtó" },
  { key: "left_rear_door", label: "Bal hátsó ajtó" },
  { key: "left_rear_fender", label: "Bal hátsó sárvédő" },
];

function getColor(value) {
  if (!value && value !== 0) return "#555";
  const v = parseInt(value);
  if (isNaN(v)) return "#555";
  if (v <= 150) return "#22c55e";
  if (v <= 250) return "#eab308";
  return "#ef4444";
}

function getBg(value) {
  if (!value && value !== 0) return "#1a1a1a";
  const v = parseInt(value);
  if (isNaN(v)) return "#1a1a1a";
  if (v <= 150) return "#0a1f0e";
  if (v <= 250) return "#1f1a05";
  return "#1f0a0a";
}

function getColorClass(value) {
  if (!value && value !== 0) return "text-white/30";
  const v = parseInt(value);
  if (isNaN(v)) return "text-white/30";
  if (v <= 150) return "text-green-400";
  if (v <= 250) return "text-yellow-400";
  return "text-red-400";
}

const ZONES = [
  { id: "right", label: "Jobb karosszéria", keys: ["right_front_fender", "right_front_door", "right_rear_door", "right_rear_fender"] },
  { id: "front", label: "Első karosszéria", keys: ["front_bumper", "hood"] },
  { id: "roof", label: "Tető", keys: ["roof"] },
  { id: "rear", label: "Hátsó karosszéria", keys: ["trunk", "rear_bumper"] },
  { id: "left", label: "Bal karosszéria", keys: ["left_front_fender", "left_front_door", "left_rear_door", "left_rear_fender"] },
];

// Measurement points on car (px, py) and their label positions (lx, ly)
// Car: front=LEFT, rear=RIGHT in the SVG
const POINTS = [
  // Right side (above car)
  { key: "right_front_fender", px: 195, py: 152, lx: 145, ly: 52, side: "right" },
  { key: "right_front_door",   px: 290, py: 150, lx: 245, ly: 22, side: "right" },
  { key: "right_rear_door",    px: 370, py: 150, lx: 325, ly: 52, side: "right" },
  { key: "right_rear_fender",  px: 440, py: 152, lx: 400, ly: 22, side: "right" },
  // Front (left of car)
  { key: "front_bumper", px: 140, py: 195, lx: 10, ly: 158, side: "front" },
  { key: "hood",         px: 182, py: 228, lx: 10, ly: 228, side: "front" },
  // Roof (center)
  { key: "roof", px: 320, py: 195, lx: 280, ly: 185, side: "roof" },
  // Rear (right of car)
  { key: "trunk",       px: 460, py: 185, lx: 500, ly: 158, side: "rear" },
  { key: "rear_bumper", px: 478, py: 228, lx: 500, ly: 228, side: "rear" },
  // Left side (below car)
  { key: "left_front_fender", px: 195, py: 248, lx: 145, ly: 320, side: "left" },
  { key: "left_front_door",   px: 290, py: 250, lx: 245, ly: 350, side: "left" },
  { key: "left_rear_door",    px: 370, py: 250, lx: 325, ly: 320, side: "left" },
  { key: "left_rear_fender",  px: 440, py: 248, lx: 400, ly: 350, side: "left" },
];

const LW = 86;
const LH = 30;

// Car outline (top-down sedan)
const CAR_BODY = "M 185,140 Q 150,140 138,160 L 128,185 Q 122,198 122,200 Q 122,202 128,215 L 138,240 Q 150,260 185,260 L 440,260 Q 475,260 486,240 L 494,218 Q 500,205 500,200 Q 500,195 494,182 L 486,160 Q 475,140 440,140 Z";

function ValueLabel({ val, x, y, color, bg, dimmed }) {
  return (
    <g opacity={dimmed ? 0.12 : 1} style={{ transition: "opacity 0.25s" }}>
      <rect x={x} y={y} width={LW} height={LH} rx="6" fill={bg} stroke={color} strokeWidth="1.2" strokeOpacity="0.6" />
      <text x={x + LW / 2} y={y + 20} textAnchor="middle" fontSize="16" fontWeight="800" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={color}>
        {val}
        <tspan fontSize="11" fontWeight="500" fill={color} fillOpacity="0.7"> µm</tspan>
      </text>
    </g>
  );
}

export function PaintDiagram({ paintThickness = {} }) {
  const [activeZone, setActiveZone] = useState(null);
  const hasData = Object.values(paintThickness).some(v => v && v !== "0" && v !== "");
  if (!hasData) return null;

  const zoneHasWarning = (keys) => keys.some(k => {
    const v = parseInt(paintThickness[k]);
    return !isNaN(v) && v > 150;
  });

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="paint-thickness-section">
      <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
        <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-[#D4AF37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        Fényezésvastagság mérés
      </h2>

      {/* Zone filter tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {ZONES.map(z => {
          const warn = zoneHasWarning(z.keys);
          const hasVals = z.keys.some(k => paintThickness[k] && paintThickness[k] !== "0");
          if (!hasVals) return null;
          return (
            <button key={z.id}
              onClick={() => setActiveZone(activeZone === z.id ? null : z.id)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                activeZone === z.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                  : warn ? "border-yellow-500/30 bg-yellow-500/5 text-yellow-400/80 hover:bg-yellow-500/10"
                  : "border-white/10 text-white/50 hover:text-white/70"
              }`}
              data-testid={`zone-tab-${z.id}`}>
              {z.label} {warn && <span className="ml-0.5 text-yellow-400">!</span>}
            </button>
          );
        })}
      </div>

      {/* Top-down car diagram */}
      <div className="w-full max-w-[620px] mx-auto mb-5 overflow-x-auto">
        <svg viewBox="0 0 600 400" className="w-full min-w-[460px]" data-testid="paint-diagram-svg">

          {/* Car body */}
          <path d={CAR_BODY} fill="#161616" stroke="#3a3a3a" strokeWidth="1.4" />

          {/* Windshield */}
          <path d="M 236,144 L 258,144 Q 264,144 264,153 L 264,247 Q 264,256 258,256 L 236,256 Q 230,256 230,247 L 230,153 Q 230,144 236,144 Z"
            fill="#0d0d0d" stroke="#333" strokeWidth="0.8" />

          {/* Rear window */}
          <path d="M 396,147 L 414,147 Q 418,147 418,156 L 418,244 Q 418,253 414,253 L 396,253 Q 392,253 392,244 L 392,156 Q 392,147 396,147 Z"
            fill="#0d0d0d" stroke="#333" strokeWidth="0.8" />

          {/* Roof panel outline */}
          <rect x="268" y="150" width="120" height="100" rx="5" fill="none" stroke="#222" strokeWidth="0.4" />

          {/* Wheels */}
          <rect x="175" y="122" width="44" height="17" rx="6" fill="#0d0d0d" stroke="#3a3a3a" strokeWidth="1" />
          <rect x="175" y="261" width="44" height="17" rx="6" fill="#0d0d0d" stroke="#3a3a3a" strokeWidth="1" />
          <rect x="415" y="122" width="44" height="17" rx="6" fill="#0d0d0d" stroke="#3a3a3a" strokeWidth="1" />
          <rect x="415" y="261" width="44" height="17" rx="6" fill="#0d0d0d" stroke="#3a3a3a" strokeWidth="1" />

          {/* Side mirrors */}
          <ellipse cx="233" cy="132" rx="9" ry="5" fill="#1a1a1a" stroke="#333" strokeWidth="0.6" />
          <ellipse cx="233" cy="268" rx="9" ry="5" fill="#1a1a1a" stroke="#333" strokeWidth="0.6" />

          {/* Headlights */}
          <rect x="120" y="178" width="7" height="12" rx="3" fill="#333" />
          <rect x="120" y="210" width="7" height="12" rx="3" fill="#333" />

          {/* Taillights */}
          <rect x="498" y="178" width="6" height="12" rx="3" fill="#441111" stroke="#553333" strokeWidth="0.5" />
          <rect x="498" y="210" width="6" height="12" rx="3" fill="#441111" stroke="#553333" strokeWidth="0.5" />

          {/* Side direction labels */}
          <text x="310" y="130" textAnchor="middle" fontSize="9" fill="#444" fontWeight="500">Jobb oldal</text>
          <text x="310" y="286" textAnchor="middle" fontSize="9" fill="#444" fontWeight="500">Bal oldal</text>

          {/* Measurement points and labels */}
          {POINTS.map(({ key, px, py, lx, ly, side }) => {
            const val = paintThickness[key];
            if (!val || val === "0" || val === "") return null;
            const color = getColor(val);
            const bg = getBg(val);
            const zone = ZONES.find(z => z.keys.includes(key));
            const dimmed = activeZone && zone && zone.id !== activeZone;

            const labelCx = lx + LW / 2;
            const labelCy = ly + LH / 2;

            return (
              <g key={key}>
                {/* Connector line */}
                <line x1={px} y1={py} x2={labelCx} y2={labelCy}
                  stroke={color} strokeWidth="0.7" strokeOpacity={dimmed ? 0.08 : 0.35} strokeDasharray="4,3"
                  style={{ transition: "stroke-opacity 0.25s" }} />
                {/* Dot on car */}
                <g opacity={dimmed ? 0.12 : 1} style={{ transition: "opacity 0.25s" }}>
                  <circle cx={px} cy={py} r="4.5" fill={color} />
                  <circle cx={px} cy={py} r="4.5" fill="none" stroke={color} strokeWidth="1" opacity="0.3">
                    <animate attributeName="r" from="4.5" to="9" dur="2.5s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.3" to="0" dur="2.5s" repeatCount="indefinite" />
                  </circle>
                </g>
                {/* Value label */}
                <ValueLabel val={val} x={lx} y={ly} color={color} bg={bg} dimmed={dimmed} />
              </g>
            );
          })}
        </svg>
      </div>

      {/* Color legend */}
      <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 pb-4 border-b border-white/5">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-green-500/30 border border-green-500" />
          <span className="text-xs text-white/50">Gyári (90-150 µm)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-yellow-500/30 border border-yellow-500" />
          <span className="text-xs text-white/50">Javított (151-250 µm)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-red-500/30 border border-red-500" />
          <span className="text-xs text-white/50">Újrafényezett (250+ µm)</span>
        </div>
      </div>

      {/* Values table */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {BODY_PARTS.map((part) => {
          const val = paintThickness[part.key];
          if (!val || val === "0" || val === "") return null;
          const zone = ZONES.find(z => z.keys.includes(part.key));
          const dimmed = activeZone && zone && zone.id !== activeZone;
          return (
            <div key={part.key} className={`flex items-center justify-between py-1 px-2 rounded bg-white/[0.02] transition-opacity ${dimmed ? "opacity-20" : ""}`}>
              <span className="text-xs text-white/60">{part.label}</span>
              <span className={`text-xs font-semibold ${getColorClass(val)}`}>{val} µm</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { BODY_PARTS };
